// Zeit dehnen und Tonhöhe ändern (Elastic Audio und AudioSuite), offline gerechnet.
// Eine Zeitkarte srcPos(k) sagt für jedes Ausgabe-Sample, welche Quellstelle (in Samples, gebrochen) dort klingen soll.
// So lassen sich gleichmäßiges Dehnen, Warp-Marker und Tempowechsel mit demselben Algorithmus rechnen.
//  · rhythmic:   WSOLA mit kurzem Fenster (Drums, Perkussion; Anschläge bleiben knackig)
//  · monophonic: WSOLA mit längerem Fenster (Gesang, Bass, Soloinstrumente)
//  · polyphonic: Phase-Vocoder mit Phasenkopplung (Akkorde, Mischungen, Pads)
//  · varispeed:  Umsampeln wie ein Bandgerät (Tonhöhe folgt der Geschwindigkeit)
// Tonhöhe (ohne Varispeed): erst um den Faktor länger dehnen, dann umsampeln.
// Vocal-Tuning (tune): vorher Tonhöhe je Stelle mit PSOLA verschieben (Länge und Klangfarbe bleiben).
import { psola } from './psola.ts';

export type StretchMode = 'rhythmic' | 'monophonic' | 'polyphonic' | 'varispeed';
export const STRETCH_MODES: { id: StretchMode; label: string; hint: string }[] = [
  { id: 'polyphonic', label: 'Polyphon', hint: 'Akkorde, ganze Mischungen, Pads' },
  { id: 'rhythmic', label: 'Rhythmisch', hint: 'Drums und Perkussion, Anschläge bleiben knackig' },
  { id: 'monophonic', label: 'Monophon', hint: 'Gesang, Bass, Soloinstrumente' },
  { id: 'varispeed', label: 'Varispeed', hint: 'wie ein Bandgerät, die Tonhöhe folgt dem Tempo' },
];

/** Kubische Interpolation (Catmull-Rom) an der gebrochenen Stelle x */
function cubic(d: Float32Array, x: number): number {
  const i = Math.floor(x), f = x - i, n = d.length;
  if (i < -1 || i > n) return 0;
  const a = d[i - 1] ?? 0, b = d[i] ?? 0, c = d[i + 1] ?? 0, e = d[i + 2] ?? 0;
  return b + 0.5 * f * (c - a + f * (2 * a - 5 * b + 4 * c - e + f * (3 * (b - c) + e - a)));
}
/** Umsampeln entlang einer Zeitkarte (Varispeed) */
function resampleMap(src: Float32Array, srcPos: (k: number) => number, outLen: number): Float32Array {
  const out = new Float32Array(outLen);
  for (let k = 0; k < outLen; k++) out[k] = cubic(src, srcPos(k));
  return out;
}
/** Gleichmäßig umsampeln um den Faktor r (Ausgabe k liest Quelle k*r) */
function resample(src: Float32Array, r: number, outLen: number): Float32Array {
  const out = new Float32Array(outLen);
  for (let k = 0; k < outLen; k++) out[k] = cubic(src, k * r);
  return out;
}
/** Ist die Karte eine reine Verschiebung (Tempo 1:1)? Dann nur kopieren. */
function isIdentity(srcPos: (k: number) => number, outLen: number): number | null {
  const o = srcPos(0);
  for (const k of [outLen >> 2, outLen >> 1, (outLen * 3) >> 2, outLen - 1]) if (Math.abs(srcPos(k) - k - o) > 0.01) return null;
  return o;
}
const hann = (n: number) => { const w = new Float32Array(n); for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / n); return w; };

// ---------------------------------------------------------------------------
// WSOLA: Fenster aus der Quelle so wählen, dass sie möglichst nahtlos an das vorige anschließen
function wsola(chs: Float32Array[], srcPos: (k: number) => number, outLen: number, N: number, lockTransients = false, soft = false): Float32Array[] {
  const Ha = N / 2, w = hann(N), tol = Math.floor(N / 12) * 4, len = chs[0].length;   // Vielfaches der Suchschrittweite, damit 0 dabei ist
  // Anschläge finden (Energiesprung > 9 dB in 64-Sample-Blöcken): Fenster mit Anschlag werden nicht verschoben,
  // damit der Anschlag genau an seiner Zielstelle bleibt (Rhythmisch)
  const B = 64, nb = Math.ceil(len / B), en = new Float32Array(nb), tr = new Uint8Array(nb);
  if (lockTransients) {
    for (let k = 0; k < nb; k++) { let e = 0; for (const c of chs) for (let i = k * B; i < Math.min(len, (k + 1) * B); i++) e += c[i] * c[i]; en[k] = e / B + 1e-10; }
    // soft (Gesang): weichere Einsätze, Anstieg > 6 dB gegen die letzten 10 ms, höchstens ein Einsatz je 60 ms
    const back = soft ? 8 : 4, ratio = soft ? 4 : 8, gap = soft ? Math.round(0.06 * 48000 / B) : 0;
    let last = -Infinity;
    for (let k = back; k < nb; k++) { let prev = 0; for (let q = 1; q <= back; q++) prev += en[k - q]; prev /= back; if (en[k] > prev * ratio && en[k] > 1e-6 && k - last > gap) { tr[k] = 1; last = k; } }
  }
  const hasTransient = (p: number) => { for (let k = Math.max(0, Math.floor((p + N / 8) / B)); k <= Math.min(nb - 1, Math.floor((p + (7 * N) / 8) / B)); k++) if (tr[k]) return true; return false; };
  const out = chs.map(() => new Float32Array(outLen + N)), norm = new Float32Array(outLen + N);
  // Suche auf einer heruntergerechneten Mono-Summe (schnell, für alle Kanäle gleich: Stereobild bleibt stabil)
  const D = 4, mono = new Float32Array(Math.ceil(len / D));
  for (let i = 0; i < mono.length; i++) { let s = 0; for (const c of chs) for (let j = 0; j < D; j++) s += c[i * D + j] ?? 0; mono[i] = s; }
  let prev = -1;
  for (let o = 0; o < outLen + Ha; o += Ha) {
    let p = Math.round(srcPos(o) - N / 2);   // Fenster ist auf Ausgabe o zentriert
    if (prev >= 0 && !(lockTransients && hasTransient(p))) {
      // natürliche Fortsetzung des vorigen Fensters und beste Übereinstimmung in ±tol um die Zielstelle
      const nat = prev + Ha, Ld = N / D;
      let best = 0, bestScore = -Infinity;
      for (let d = -tol; d <= tol; d += D) {
        const q = p + d;
        if (lockTransients && d !== 0 && hasTransient(q)) continue;   // keinen Anschlag aus seiner Lage ziehen
        let s = 0, e1 = 1e-9, e2 = 1e-9;
        for (let i = 0; i < Ld; i += 2) {
          const a = mono[((nat / D) | 0) + i] ?? 0, b = mono[((q / D) | 0) + i] ?? 0;
          s += a * b; e1 += a * a; e2 += b * b;
        }
        const score = s / Math.sqrt(e1 * e2) - Math.abs(d) / (tol * 40);
        if (score > bestScore) { bestScore = score; best = d; }
      }
      p += best;
    }
    for (let c = 0; c < chs.length; c++) { const src = chs[c], dst = out[c]; for (let i = 0; i < N && o + i < dst.length; i++) { const si = p + i; if (si >= 0 && si < len) dst[o + i] += src[si] * w[i]; } }
    for (let i = 0; i < N && o + i < norm.length; i++) norm[o + i] += w[i];
    prev = p;
  }
  // Puffer ist um N/2 versetzt (Fenster zentriert)
  return out.map((d) => { const r = new Float32Array(outLen); for (let k = 0; k < outLen; k++) { const n = norm[k + N / 2]; r[k] = n > 1e-3 ? d[k + N / 2] / n : 0; } return r; });
}

// ---------------------------------------------------------------------------
// Phase-Vocoder mit Phasenkopplung (identity phase locking)
export function fft(re: Float64Array, im: Float64Array, inv = false) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) { let bit = n >> 1; for (; j & bit; bit >>= 1) j ^= bit; j ^= bit; if (i < j) { [re[i], re[j]] = [re[j], re[i]]; [im[i], im[j]] = [im[j], im[i]]; } }
  for (let size = 2; size <= n; size <<= 1) {
    const ang = ((inv ? 2 : -2) * Math.PI) / size, wr = Math.cos(ang), wi = Math.sin(ang);
    for (let s = 0; s < n; s += size) {
      let cr = 1, ci = 0;
      for (let k = 0; k < size / 2; k++) {
        const a = s + k, b = a + size / 2, tr = re[b] * cr - im[b] * ci, ti = re[b] * ci + im[b] * cr;
        re[b] = re[a] - tr; im[b] = im[a] - ti; re[a] += tr; im[a] += ti;
        const t = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = t;
      }
    }
  }
  if (inv) for (let i = 0; i < n; i++) { re[i] /= n; im[i] /= n; }
}
function phaseVocoder(src: Float32Array, srcPos: (k: number) => number, outLen: number, N: number): Float32Array {
  const Hs = N / 4, w = hann(N), bins = N / 2 + 1, len = src.length;
  const out = new Float32Array(outLen + N), norm = new Float32Array(outLen + N);
  const lastPhase = new Float64Array(bins), sumPhase = new Float64Array(bins);
  const re = new Float64Array(N), im = new Float64Array(N), mag = new Float64Array(bins), ph = new Float64Array(bins);
  let prevA = NaN;
  for (let o = 0; o < outLen + Hs; o += Hs) {
    const a = srcPos(o) - N / 2;   // Fenster ist auf Ausgabe o zentriert
    const ai = Math.floor(a), af = a - ai;
    for (let i = 0; i < N; i++) { const j = ai + i; const v = j >= 0 && j + 1 < len ? src[j] + af * (src[j + 1] - src[j]) : 0; re[i] = v * w[i]; im[i] = 0; }
    fft(re, im);
    for (let k = 0; k < bins; k++) { mag[k] = Math.hypot(re[k], im[k]); ph[k] = Math.atan2(im[k], re[k]); }
    const Ha = Number.isNaN(prevA) ? Hs : a - prevA;
    // Spitzen finden, deren Phase fortschreiben, die Nachbarn daran koppeln
    if (Number.isNaN(prevA)) for (let k = 0; k < bins; k++) sumPhase[k] = ph[k];
    else {
      const peaks: number[] = [];
      for (let k = 2; k < bins - 2; k++) if (mag[k] > mag[k - 1] && mag[k] >= mag[k + 1] && mag[k] > mag[k - 2] && mag[k] >= mag[k + 2]) peaks.push(k);
      if (!peaks.length) peaks.push(1);
      const adv = new Float64Array(bins);
      for (const k of peaks) {
        const omega = (2 * Math.PI * k) / N;
        let d = ph[k] - lastPhase[k] - omega * Ha;
        d -= 2 * Math.PI * Math.round(d / (2 * Math.PI));
        const inst = omega + d / Math.max(1e-9, Ha);
        adv[k] = sumPhase[k] + inst * Hs;
      }
      let pi = 0;
      for (let k = 0; k < bins; k++) {
        while (pi + 1 < peaks.length && Math.abs(peaks[pi + 1] - k) < Math.abs(peaks[pi] - k)) pi++;
        const pk = peaks[pi];
        sumPhase[k] = k === pk ? adv[k] : adv[pk] + (ph[k] - ph[pk]);
      }
    }
    for (let k = 0; k < bins; k++) lastPhase[k] = ph[k];
    prevA = a;
    for (let k = 0; k < bins; k++) { re[k] = mag[k] * Math.cos(sumPhase[k]); im[k] = mag[k] * Math.sin(sumPhase[k]); }
    for (let k = 1; k < N / 2; k++) { re[N - k] = re[k]; im[N - k] = -im[k]; }
    fft(re, im, true);
    for (let i = 0; i < N; i++) { const idx = o + i; if (idx < out.length) { out[idx] += re[i] * w[i]; norm[idx] += w[i] * w[i]; } }
  }
  const r = new Float32Array(outLen);
  for (let k = 0; k < outLen; k++) { const n = norm[k + N / 2]; r[k] = n > 1e-3 ? out[k + N / 2] / n : 0; }
  return r;
}

// ---------------------------------------------------------------------------
export interface StretchJob {
  channels: Float32Array[];
  sampleRate: number;
  /** Quellstelle (Samples) für Ausgabe-Sample k */
  srcPos: (k: number) => number;
  outLen: number;
  mode: StretchMode;
  /** Tonhöhe in Halbtönen (Varispeed ignoriert das) */
  semitones?: number;
  /** Vocal-Tuning: Grundfrequenz und Verschiebung (Halbtöne) je Rahmen; Rahmen 0 beginnt bei Sample origin, Abstand hopS */
  tune?: TuneData;
}
export interface TuneData { f0: Float32Array; shift: Float32Array; hopS: number; origin: number }
export function renderStretch(j: StretchJob): Float32Array[] {
  const { srcPos, outLen, mode, sampleRate: sr } = j;
  let channels = j.channels;
  if (j.tune) {
    const T = j.tune, at = (arr: Float32Array) => (i: number) => { const k = Math.floor((i - T.origin) / T.hopS); return k >= 0 && k < arr.length ? arr[k] : 0; };
    channels = psola(channels, sr, at(T.f0), at(T.shift));
  }
  const pr = mode === 'varispeed' ? 1 : Math.pow(2, (j.semitones ?? 0) / 12);
  if (mode === 'varispeed') return channels.map((c) => resampleMap(c, srcPos, outLen));
  if (Math.abs(pr - 1) < 1e-6) {
    const off = isIdentity(srcPos, outLen);
    if (off !== null && Math.abs(off - Math.round(off)) < 1e-6) return channels.map((c) => { const r = new Float32Array(outLen); for (let k = 0; k < outLen; k++) r[k] = c[k + Math.round(off)] ?? 0; return r; });
  }
  // Mit Tonhöhe: auf pr-fache Länge dehnen, dann um pr umsampeln
  const L = Math.ceil(outLen * pr) + 4, map = pr === 1 ? srcPos : (q: number) => srcPos(q / pr);
  const pow2 = (ms: number) => { let n = 256; while (n < (ms / 1000) * sr) n <<= 1; return n; };
  let stretched: Float32Array[];
  if (mode === 'polyphonic') stretched = channels.map((c) => phaseVocoder(c, map, L, pow2(80)));
  // Rhythmisch: kurzes Fenster und nur wenig Spielraum, damit Anschläge genau an ihrer Stelle bleiben
  // Monophon (Gesang): längeres Fenster, Einsätze werden ebenfalls festgehalten, damit Silben genau auf ihrem Ziel landen
  else stretched = mode === 'rhythmic' ? wsola(channels, map, L, pow2(18), true) : wsola(channels, map, L, pow2(40), true, true);
  return pr === 1 ? stretched.map((c) => c.subarray(0, outLen)) : stretched.map((c) => resample(c, pr, outLen));
}

/** Zeitkarte aus Stützpunkten (Ausgabe-Sample → Quell-Sample), dazwischen linear, außen mit der Steigung des Randstücks */
export function pointMap(points: { out: number; src: number }[]): (k: number) => number {
  const pts = [...points].sort((a, b) => a.out - b.out);
  if (pts.length === 1) return (k) => pts[0].src + (k - pts[0].out);
  return (k) => {
    let i = 0;
    while (i < pts.length - 2 && k > pts[i + 1].out) i++;
    const a = pts[i], b = pts[i + 1], slope = (b.src - a.src) / Math.max(1e-9, b.out - a.out);
    return a.src + (k - a.out) * slope;
  };
}
